import {
    Alert,
    Box,
    Divider,
    Heading,
    Icons,
    Stack,
    ThemeColorVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import Portal from "components/portal/portal";
import ActivityStepContinueButton from "components/selection-components/select-activity/activity-step-continue-button";
import Summary from "components/selection-components/summary/summary";
import { usePaymentRule } from "hooks/use-payment-rule";
import { t } from "i18next";
import { CSSVarHeaderHeight } from "style-variables";
import { PaymentRule } from "types/payment";

interface ISummaryAside {
    handleOpenActivity: (ruleId: number) => void;
    handleOpenExperience: (experienceId: number) => void;
    gotoDetailsForm: () => void;
}

const SummaryAside = ({
    handleOpenActivity,
    handleOpenExperience,
    gotoDetailsForm,
}: ISummaryAside) => {
    const paymentRule = usePaymentRule();
    const { data: reservation } = useWebBooking();

    const getAlertText = () => {
        switch (paymentRule) {
            case PaymentRule.Prepayment:
                return t("activitySummary.alert.prepayment");
            case PaymentRule.NoShowFee:
                return t("activitySummary.alert.paymentCardRegistration");
            default:
                return t("activitySummary.alert.paymentAtLocation");
        }
    };
    const headerDate = t("time.date", {
        value: reservation?.start.unix(),
    });

    return (
        <Portal containerId="aside-portal-container">
            <Box
                as="aside"
                borderLeft={`1px solid ${ThemeColorVariable.OnBackgroundBorder}`}
                h={`calc(100vh - ${CSSVarHeaderHeight})`}
                display={{
                    base: "none",
                    lg: (reservation?.articles?.length ?? 0) > 0 ? "flex" : "none",
                }}
                flexDirection="column"
                overflow="hidden"
            >
                {/* Header - Fixed at top */}
                <Box
                    pt={ThemeSpaceVariable.Large}
                    pb={ThemeSpaceVariable.Medium}
                    px={ThemeSpaceVariable.Medium}
                    flexShrink={0}
                >
                    <Heading as="h3" fontWeight="500" fontSize="xl">
                        {t("activitySummary.title", { date: headerDate })}
                    </Heading>
                </Box>

                {/* Scrollable content area */}
                <Box
                    flex={1}
                    overflowY="auto"
                    px={ThemeSpaceVariable.Medium}
                    pb={ThemeSpaceVariable.Medium}
                    position="relative"
                >
                    <Summary
                        openActivity={handleOpenActivity}
                        openExperience={handleOpenExperience}
                    />
                    <Divider />
                </Box>

                {/* Footer - Fixed at bottom */}
                <Box flexShrink={0} px={ThemeSpaceVariable.Medium} py={ThemeSpaceVariable.Medium}>
                    <Stack gap={ThemeSpaceVariable.Medium}>
                        <Alert type="info" icon={Icons.PaymentCard}>
                            {getAlertText()}
                        </Alert>
                        <ActivityStepContinueButton onClick={gotoDetailsForm} />
                    </Stack>
                </Box>
            </Box>
        </Portal>
    );
};

export default SummaryAside;
